import React, { useMemo, useRef, useState } from "react";
import {
    Button, Card, Empty, Input, List, Space, Tag,
    Typography, Divider, Statistic, Row, Col,
} from "antd";
import {
    PrinterOutlined, SearchOutlined, TeamOutlined,
    CheckCircleOutlined, CloseCircleOutlined,
    ClockCircleOutlined, FileDoneOutlined, ManOutlined, WomanOutlined,
} from "@ant-design/icons";

import { URLS } from "@/lib/urls";
import AdminLayout from "@/components/layout/admin/adminLayout";
import {
    useApiCreateAdminProductDeliveryActionDeliverToLesson,
    useApiSearchAdminProductsDeliveryToLesson,
} from "@/api/product";
import { useReactToPrint } from "react-to-print";
import RenderIf from "@/components/render-if";
import ApiHandleSuccessMessage, { ApiHandleErrorMessage } from "@/lib/helper/apiHandleResultMessage";
import { getJalaliDateTime } from "@/lib/utils/date";
import ProductReceiptPrintV3 from "@/pages/admin/products/delivery/print-lesson/_section/Print";

const { Title, Text } = Typography;

/* ---------- helpers ---------- */

const STUDENT_STATUS = {
    DELIVERED: "delivered",
    PRINTED: "printed",
    PENDING: "pending",
    DROPPED: "dropped",
    NO_ITEM: "no_item",
};

const STATUS_META = {
    [STUDENT_STATUS.DELIVERED]: { label: "تحویل گرفته", color: "green", icon: <CheckCircleOutlined /> },
    [STUDENT_STATUS.PRINTED]:   { label: "چاپ شده",     color: "blue",  icon: <FileDoneOutlined /> },
    [STUDENT_STATUS.PENDING]:   { label: "در انتظار تحویل", color: "orange", icon: <ClockCircleOutlined /> },
    [STUDENT_STATUS.DROPPED]:   { label: "انصراف داده", color: "red",   icon: <CloseCircleOutlined /> },
    [STUDENT_STATUS.NO_ITEM]:   { label: "قلمی ثبت نشده", color: "default", icon: <CloseCircleOutlined /> },
};

/* ---------- component ---------- */

function AdminProductReceiptSearchAndPrintToLesson() {
    const [keyword, setKeyword] = useState("");
    const [search, setSearch] = useState("");

    const printRef = useRef(null);

    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: "Receipt",
    });

    const { data, isLoading, refetch } = useApiSearchAdminProductsDeliveryToLesson({
        search: search,
        type: "print",
    });

    const { mutate: deliveryAction, isPending: isPendingAction } =
        useApiCreateAdminProductDeliveryActionDeliverToLesson();

    /* ---------- استخراج داده‌ها ---------- */

    const payload = data?.data || {};
    const lessonLists = payload?.lesson_lists || [];
    const deliverStepLessons = payload?.deliver_step_lessons || [];
    const deliverListByLesson = payload?.deliver_list_by_lesson || [];

    /* ---------- ساخت ساختار کلاس‌ها با محاسبات ---------- */

    const classes = useMemo(() => {
        // نگاشت lesson_id -> deliveries
        const deliveriesMap = new Map();
        deliverListByLesson.forEach((item) => {
            deliveriesMap.set(item.lesson_id, item.deliveries || []);
        });

        // نگاشت lesson_id -> lesson_lists
        const lessonListsMap = new Map();
        lessonLists.forEach((item) => {
            if (!lessonListsMap.has(item.lesson_id)) lessonListsMap.set(item.lesson_id, []);
            lessonListsMap.get(item.lesson_id).push(item);
        });

        // نگاشت lesson_id -> deliver_step_lessons (اطلاعات مربی)
        const stepMap = new Map();
        deliverStepLessons.forEach((item) => stepMap.set(item.lesson_id, item));

        // همه lesson_id های درگیر
        const allLessonIds = new Set([
            ...lessonListsMap.keys(),
            ...deliveriesMap.keys(),
            ...stepMap.keys(),
        ]);

        return Array.from(allLessonIds).map((lessonId) => {
            const step = stepMap.get(lessonId);
            const lesson = step?.lesson;
            const students = lessonListsMap.get(lessonId) || [];
            const deliveries = deliveriesMap.get(lessonId) || [];

            // نگاشت useracademy_id -> delivery
            const deliveryByStudent = new Map();
            deliveries.forEach((d) => deliveryByStudent.set(d.useracademy_id, d));

            // نگاشت student_id -> student_detail از لیست کلاس
            const classStudentIds = new Set(students.map((s) => s.student));

            const rows = [];

            // 1) همه دانش‌آموزان لیست کلاس
            students.forEach((s) => {
                const delivery = deliveryByStudent.get(s.student);
                const hasDelivery = !!delivery;
                const isDelivered = delivery?.status === "delivered";

                let status;
                if (isDelivered) {
                    status = STUDENT_STATUS.DELIVERED;
                } else if (hasDelivery) {
                    status = STUDENT_STATUS.PENDING;
                } else {
                    status = STUDENT_STATUS.NO_ITEM;
                }

                rows.push({
                    key: `s-${s.student}`,
                    studentId: s.student,
                    detail: s.student_detail,
                    delivery: delivery || null,
                    status,
                    inClassList: true,
                    inDeliveryList: hasDelivery,
                });
            });

            // 2) افرادی که در لیست تحویل هستند ولی در لیست کلاس نیستند (انصراف)
            deliveries.forEach((d) => {
                if (!classStudentIds.has(d.useracademy_id)) {
                    rows.push({
                        key: `d-${d.useracademy_id}`,
                        studentId: d.useracademy_id,
                        detail: d.useracademy,
                        delivery: d,
                        status: STUDENT_STATUS.DROPPED,
                        inClassList: false,
                        inDeliveryList: true,
                    });
                }
            });

            // محاسبه تعداد اقلام قابل تحویل:
            // کسانی که در لیست کلاس هستند + در لیست تحویل هستند + تحویل نگرفته‌اند
            const deliverableCount = rows.filter(
                (r) => r.inClassList && r.inDeliveryList && r.status === STUDENT_STATUS.PENDING
            ).length;

            const stats = {
                deliverableCount,
                teacherDelivered: step?.delivered_teacher != null,
                stepCode: step?.code,
                stepStatus: step?.status,
                teacher: lesson?.teacher_detail,
                lessonCode: lesson?.code,
                female: rows.filter((r) => r.detail?.gender === "female")
                    .filter((r) => r.inClassList && r.inDeliveryList && r.status === STUDENT_STATUS.PENDING).length,
                male: rows.filter((r) => r.detail?.gender === "male")
                    .filter((r) => r.inClassList && r.inDeliveryList && r.status === STUDENT_STATUS.PENDING).length,
            };

            return {
                lessonId,
                lesson,
                step,
                rows,
                stats,
            };
        });
    }, [lessonLists, deliverStepLessons, deliverListByLesson, search]);

    /* ---------- چاپ رسید ---------- */

    function handlePrintReceipt() {
        if (!payload?.teacher_id) return null;

        deliveryAction(
            {
                action: "print",
                teacher_id: payload?.teacher_id,
            },
            {
                onSuccess: (r) => {
                    ApiHandleSuccessMessage(r);
                    refetch();
                    handlePrint();
                },
                onError: (e) => ApiHandleErrorMessage(e),
            }
        );
    }

    return (
        <AdminLayout
            backTo={URLS.admin.products.dashboard}
            pageTitle="صدور رسید تحویل اقلام به مربیان"
        >
            <div className="flex items-center justify-center p-6">
                <Card
                    className="w-full max-w-6xl rounded-3xl shadow-xl border-0"
                    bodyStyle={{ padding: 40 }}
                >
                    {/* سرچ */}
                    <div className="text-center mb-10">
                        <Title level={2} className="!mb-2">
                            جستجوی مربی دریافت‌کننده اقلام آموزشی
                        </Title>
                        <Text type="secondary">
                            شماره موبایل یا کد تحویل مربی را وارد کنید.
                        </Text>

                        <div className="mt-8 flex justify-center">
                            <Input.Search
                                size="large"
                                allowClear
                                enterButton={
                                    <>
                                        <SearchOutlined /> جستجو
                                    </>
                                }
                                loading={isLoading}
                                value={keyword}
                                placeholder="جستجو کنید ..."
                                className="max-w-xl"
                                onChange={(e) => setKeyword(e.target.value)}
                                onSearch={(v) => setSearch(v.trim())}
                            />
                        </div>
                    </div>

                    {/* هنوز سرچ نشده */}
                    {!search && (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="برای مشاهده اطلاعات، یک شماره موبایل یا کد ملی مربی جستجو کنید."
                        />
                    )}

                    {search && classes.length === 0 && (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_DEFAULT}
                            description="مورد جستجو شده یافت نشد!"
                        />
                    )}

                    {/* کلاس‌ها */}
                    {classes.map((cls) => (
                        <ClassBlock key={cls.lessonId} cls={cls} />
                    ))}

                    {/* دکمه چاپ */}
                    {classes.length > 0 && (
                        <div className="text-center pt-6">
                            <Button
                                onClick={handlePrintReceipt}
                                size="large"
                                type="primary"
                                loading={isPendingAction}
                                icon={<PrinterOutlined />}
                            >
                                چاپ رسید تحویل
                            </Button>
                        </div>
                    )}
                </Card>
            </div>

            <div className="flex justify-center">
                <div ref={printRef}>
                    <ProductReceiptPrintV3
                        classes={classes}
                        payload={payload}
                        keyword={search}
                    />
                </div>
            </div>
        </AdminLayout>
    );
}

/* ---------- زیرکامپوننت نمایش هر کلاس ---------- */

function ClassBlock({ cls }) {
    const { lesson, rows, stats } = cls;

    return (
        <Card
            className="mb-6 rounded-2xl"
            title={
                <Space wrap>
                    <TeamOutlined />
                    <span>
                        کلاس {lesson?.code ?? "-"} — مربی:{" "}
                        {stats.teacher?.firstname} {stats.teacher?.lastname}
                    </span>
                    <RenderIf condition={stats.stepStatus === 'delivered'}>
                        <Tag color="green">تحویل به مربی انجام شده</Tag>
                    </RenderIf>
                    <RenderIf condition={stats.stepStatus === 'pending'}>
                        <Tag color="orange">در انتظار تحویل به مربی</Tag>
                    </RenderIf>
                    <RenderIf condition={stats.stepStatus === 'print'}>
                        <Tag color="blue">رسید تحویل به مربی چاپ شده است</Tag>
                    </RenderIf>
                    {stats.stepCode && <Tag>کد: {stats.stepCode}</Tag>}
                </Space>
            }
        >
            {/* فقط تعداد اقلام قابل تحویل */}
            <Row gutter={[12, 12]} className="mb-4">
                <Col xs={24} sm={12} md={8}>
                    <Statistic
                        title="اقلام قابل تحویل"
                        value={stats.deliverableCount}
                        valueStyle={{ color: "#fa8c16", fontSize: 28 }}
                    />
                </Col>
                <Col xs={24} sm={12} md={16}>
                    <Space wrap className="mt-2">
                        <Tag icon={<WomanOutlined />} color="magenta">
                            دختر: {stats.female}
                        </Tag>
                        <Tag icon={<ManOutlined />} color="blue">
                            پسر: {stats.male}
                        </Tag>
                    </Space>
                </Col>
            </Row>

            <Divider />

            {/* لیست دانش‌آموزان */}
            <List
                itemLayout="horizontal"
                dataSource={rows}
                renderItem={(row) => {
                    const meta = STATUS_META[row.status];
                    const d = row.detail || {};
                    return (
                        <List.Item
                            actions={[
                                <Tag key="status" color={meta.color} icon={meta.icon}>
                                    {meta.label}
                                </Tag>,
                            ]}
                        >
                            <List.Item.Meta

                                title={
                                    <Space wrap>
                                        <span>
                                            {d?.firstname} {d?.lastname}
                                        </span>
                                        {d?.gender === "female" && (
                                            <Tag color="magenta" icon={<WomanOutlined />}>دختر</Tag>
                                        )}
                                        {d?.gender === "male" && (
                                            <Tag color="blue" icon={<ManOutlined />}>پسر</Tag>
                                        )}
                                        <RenderIf condition={row.stepStatus === 'delivered'}>
                                            <Tag color="red">قبلا تحویل گرفته است</Tag>
                                        </RenderIf>
                                        {/* در لیست کلاس نیست */}
                                        <RenderIf condition={!row.inClassList}>
                                            <Tag color="red">در لیست کلاس نیست</Tag>
                                        </RenderIf>
                                        {/* در لیست تحویل نیست */}
                                        <RenderIf condition={row.inClassList && !row.inDeliveryList}>
                                            <Tag color="volcano">در لیست اقلام نیست</Tag>
                                        </RenderIf>
                                    </Space>
                                }
                                description={
                                    <>
                                        <Space wrap>
                                            {d?.code && <Tag color="blue">کد: {d.code}</Tag>}
                                            {row.delivery?.code && (
                                                <Tag color="geekblue">کد تحویل به فرد: {row.delivery.code}</Tag>
                                            )}
                                        </Space>
                                        {/* هیستوری */}
                                        {row.delivery?.histories?.length > 0 && (
                                            <div className="  mt-2 flex flex-col text-xs">
                                                {row.delivery.histories.map((h) => (
                                                    <div key={h.id} className="flex flex-wrap gap-2 mb-1 items-center">
                                                        <RenderIf condition={h.action === "print"}>
                                                            <Tag color="blue">چاپ شده</Tag>
                                                        </RenderIf>
                                                        <RenderIf condition={h.action === "print_to_teacher"}>
                                                            <Tag color="cyan">چاپ برای مربی</Tag>
                                                        </RenderIf>
                                                        <RenderIf condition={h.action === "re_print_to_teacher"}>
                                                            <Tag color="cyan">چاپ مجدد برای مربی</Tag>
                                                        </RenderIf>
                                                        <RenderIf condition={h.action === "reprint"}>
                                                            <Tag color="geekblue">چاپ مجدد</Tag>
                                                        </RenderIf>
                                                        <RenderIf condition={h.action === "notif"}>
                                                            <Tag color="pink" title={h?.meta?.notif_text}>
                                                                ارسال پیامک
                                                            </Tag>
                                                        </RenderIf>
                                                        <RenderIf condition={h.action === "deliver"}>
                                                            <Tag color="green">تحویل داده شده</Tag>
                                                        </RenderIf>
                                                        <RenderIf condition={h.action === "deliver_to_teacher"}>
                                                            <Tag color="green">تحویل به مربی</Tag>
                                                        </RenderIf>
                                                        <span>
                                                            {h?.expert?.firstname} {h?.expert?.lastname}
                                                        </span>
                                                        <span>{getJalaliDateTime(h.datetime)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                }
                            />
                        </List.Item>
                    );
                }}
            />
        </Card>
    );
}

export default AdminProductReceiptSearchAndPrintToLesson;